import React from "react";
import { ChevronRight } from "lucide-react";
import type { AssistantSegment } from "../utils/assistantSegments";
import {
  ACTIVITY_EMPTY_ANSWER,
  ACTIVITY_REASONING_HIDE,
  ACTIVITY_REASONING_SHOW,
  ACTIVITY_REASONING_TRANSLATING,
  ACTIVITY_THINKING,
} from "../utils/activityTexts.de";
import {
  THINKING_STATUS_LABEL,
  assistantReasoningDisclosureText,
  assistantStatusLine,
  statusLineReplaceDelayMs,
} from "../utils/assistantStatusLine";
import { useGuardedReasoningSegments } from "../utils/useGuardedReasoningSegments";

function ReasoningDisclosure({
  open,
  onToggle,
  text,
  pending,
}: {
  open: boolean;
  onToggle: () => void;
  text: string;
  pending: boolean;
}) {
  return (
    <div className="mt-2">
      <button
        type="button"
        className="inline-flex items-center gap-1 text-sm text-app-text-subtle"
        onClick={onToggle}
        aria-expanded={open}
      >
        <ChevronRight className={`h-3.5 w-3.5 shrink-0 transition-transform ${open ? "rotate-90" : ""}`} />
        {open ? ACTIVITY_REASONING_HIDE : ACTIVITY_REASONING_SHOW}
      </button>
      {open ? (
        <div className="mt-1.5 whitespace-pre-wrap font-mono text-[11px] leading-relaxed text-app-text-subtle">
          {[text, pending ? ACTIVITY_REASONING_TRANSLATING : ""].filter(Boolean).join("\n\n")}
        </div>
      ) : null}
    </div>
  );
}

/** Keeps one status string on screen; swaps only after the minimum dwell. */
function useStableStatusLine(target: string | null): string | null {
  const [shown, setShown] = React.useState<string | null>(target);
  const shownRef = React.useRef<string | null>(target);
  const shownAtRef = React.useRef(0);
  const targetRef = React.useRef(target);

  React.useEffect(() => {
    targetRef.current = target;
    if (target === null) {
      shownRef.current = null;
      setShown(null);
      return;
    }
    if (shownRef.current === null) {
      shownRef.current = target;
      shownAtRef.current = Date.now();
      setShown(target);
      return;
    }
    if (shownRef.current === target) return;

    const wait = statusLineReplaceDelayMs(shownAtRef.current, Date.now());
    const timer = window.setTimeout(() => {
      const next = targetRef.current;
      if (next === null) {
        shownRef.current = null;
        setShown(null);
        return;
      }
      shownRef.current = next;
      shownAtRef.current = Date.now();
      setShown(next);
    }, wait);
    return () => window.clearTimeout(timer);
  }, [target]);

  return shown;
}

export function AssistantTimeline({
  segments,
  renderText,
  isSending,
}: {
  segments: AssistantSegment[] | undefined;
  renderText: (text: string) => React.ReactNode;
  isSending: boolean;
}) {
  // Reasoning is translated only while it is visible: live in the status line, or opened.
  const [reasoningOpen, setReasoningOpen] = React.useState(false);
  const { segments: list, pending } = useGuardedReasoningSegments(segments, isSending, isSending || reasoningOpen);
  const heldTextRef = React.useRef(THINKING_STATUS_LABEL);
  const derived = assistantStatusLine(list, isSending, heldTextRef.current);
  if (derived) heldTextRef.current = derived;
  const status = useStableStatusLine(isSending ? (derived ?? heldTextRef.current) : null);
  const answers = list.filter(
    (segment): segment is Extract<AssistantSegment, { type: "text" }> =>
      segment.type === "text" && Boolean(segment.text.trim()),
  );
  const hasReasoning = !isSending && Boolean(assistantReasoningDisclosureText(segments));
  const reasoningText = hasReasoning && reasoningOpen ? assistantReasoningDisclosureText(list) : "";

  if (!isSending && answers.length === 0) {
    return <span className="italic text-app-text-subtle">{ACTIVITY_EMPTY_ANSWER}</span>;
  }

  return (
    <div>
      {isSending ? (
        <p className="chat-status-shimmer text-sm">{status ?? ACTIVITY_THINKING}</p>
      ) : null}
      {answers.length > 0 ? (
        <div className={isSending ? "mt-2 space-y-2" : "space-y-2"}>
          {answers.map((segment, index) => (
            <div key={`x-${index}`} className="text-sm text-app-text-muted leading-relaxed">
              {renderText(segment.text)}
            </div>
          ))}
        </div>
      ) : null}
      {hasReasoning ? (
        <ReasoningDisclosure
          open={reasoningOpen}
          onToggle={() => setReasoningOpen((value) => !value)}
          text={reasoningText}
          pending={pending}
        />
      ) : null}
    </div>
  );
}
